import { useState, useMemo, useCallback } from 'react';
import Page from 'src/components/page/Page';
import {
  ColumnDef,
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
} from '@tanstack/react-table';
import { useCustomerProductMatches } from '@/api/rpc/hooks/accounts-receivable/useCustomerProductMatches';
import type { CustomerProductMatch } from '@erp/rpc';
import { DataTable } from '@/components/common/data-table/data-table';
import { DataTableToolbar } from '@/components/common/data-table/data-table-toolbar';
import { DataTablePagination } from '@/components/common/data-table/data-table-pagination';
import { Card } from '@/components/ui/card';
import { format } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { CustomersAutocomplete } from '@/components/customer/CustomersAutocomplete';
import { ProductsV2Autocomplete } from '@/components/products/ProductsV2Autocomplete';
import CustomerPill from '@/components/customer/CustomerPill';
import ProductPill from '@/components/products/ProductV2Pill';
import { XIcon } from '@phosphor-icons/react';

interface Filters {
  customerId?: string;
  productId?: string;
}

const columns: ColumnDef<CustomerProductMatch>[] = [
  {
    accessorKey: 'customer.name',
    header: 'Customer',
    cell: ({ row }) => {
      return <div className="font-medium">{row.original.customer.name}</div>;
    },
    size: 200,
  },
  {
    accessorKey: 'customerFreeform',
    header: 'Customer Terms',
    cell: ({ row }) => {
      const freeform = row.original.customerFreeform;
      if (!freeform || freeform.length === 0) {
        return (
          <span className="text-muted-foreground text-sm italic">No terms</span>
        );
      }
      return (
        <div className="flex flex-wrap gap-1">
          {freeform.map((term, idx) => (
            <Badge key={idx} variant="secondary">
              {term}
            </Badge>
          ))}
        </div>
      );
    },
    size: 300,
  },
  {
    accessorKey: 'product.name',
    header: 'Maps to Product',
    cell: ({ row }) => {
      const variant = row.original.productVariant;
      return (
        <div className="space-y-1">
          <div className="font-medium">{row.original.product.name}</div>
          {variant.name && (
            <div className="text-sm text-muted-foreground">{variant.name}</div>
          )}
          {variant.sku && (
            <div className="text-xs text-muted-foreground">
              SKU: {variant.sku}
            </div>
          )}
        </div>
      );
    },
    size: 300,
  },
  {
    accessorKey: 'updatedAt',
    header: 'Last Updated',
    cell: ({ getValue }) => {
      const date = getValue() as Date;
      return (
        <div className="text-sm text-muted-foreground">
          {format(new Date(date), 'MMM d, yyyy')}
        </div>
      );
    },
    size: 120,
  },
];

function CustomerProductMatchesTable({
  filters,
  onFiltersChange,
}: {
  filters: Filters;
  onFiltersChange: (filters: Filters) => void;
}) {
  const [paginationState, setPaginationState] = useState({
    pageIndex: 0,
    pageSize: 25,
  });

  const queryFilters = useMemo(
    () => ({
      customerId: filters.customerId,
      productId: filters.productId,
      limit: paginationState.pageSize,
      offset: paginationState.pageIndex * paginationState.pageSize,
    }),
    [filters, paginationState],
  );

  const { customerProductMatches, pagination, isLoading, data } =
    useCustomerProductMatches(queryFilters);

  const table = useReactTable({
    columns,
    data: customerProductMatches,
    manualPagination: true,
    pageCount: pagination ? Math.ceil(pagination.total / pagination.limit) : 0,
    state: {
      pagination: paginationState,
    },
    onPaginationChange: setPaginationState,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  // Show loader if we're loading and have no data yet
  const showLoader = !data;

  const onCustomerSelect = useCallback(
    (customerId: string | undefined) => {
      onFiltersChange({
        ...filters,
        customerId,
      });
      setPaginationState({ pageIndex: 0, pageSize: paginationState.pageSize });
    },
    [filters, onFiltersChange, paginationState.pageSize],
  );

  const onProductSelect = useCallback(
    (productId: string | undefined) => {
      onFiltersChange({
        ...filters,
        productId,
      });
      setPaginationState({ pageIndex: 0, pageSize: paginationState.pageSize });
    },
    [filters, onFiltersChange, paginationState.pageSize],
  );

  const isCustomFiltered = useMemo(() => {
    return Boolean(filters.customerId || filters.productId);
  }, [filters]);

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <DataTableToolbar table={table}>
          <CustomersAutocomplete
            selectedCustomerId={filters.customerId}
            onCustomerSelect={onCustomerSelect}
            allowClear
            className="w-[200px]"
          />
          <ProductsV2Autocomplete
            selectedProductId={filters.productId}
            onProductSelect={onProductSelect}
            allowClear
            className="w-[200px]"
          />
        </DataTableToolbar>

        {/* Active filter pills */}
        {isCustomFiltered && (
          <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t items-center">
            {filters.customerId && (
              <div className="flex items-center gap-2">
                <CustomerPill customerId={filters.customerId} />
                <button
                  onClick={() => onCustomerSelect(undefined)}
                  className="hover:text-destructive -ml-1"
                  title="Remove filter"
                >
                  <XIcon className="h-4 w-4" />
                </button>
              </div>
            )}
            {filters.productId && (
              <div className="flex items-center gap-2">
                <ProductPill productId={filters.productId} />
                <button
                  onClick={() => onProductSelect(undefined)}
                  className="hover:text-destructive -ml-1"
                  title="Remove filter"
                >
                  <XIcon className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        )}
      </Card>

      {showLoader ? (
        <Card className="p-8">
          <div className="flex flex-col items-center justify-center space-y-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            <div className="text-center space-y-2">
              <p className="text-lg font-medium">
                Loading customer product matches...
              </p>
              <p className="text-sm text-muted-foreground">
                This may take a while
              </p>
            </div>
          </div>
        </Card>
      ) : (
        <>
          <Card>
            <DataTable table={table} />
            <div className="border-t">
              <DataTablePagination table={table} />
            </div>
          </Card>

          {pagination && (
            <div className="text-sm text-muted-foreground text-center">
              Showing {pagination.offset + 1} to{' '}
              {Math.min(pagination.offset + pagination.limit, pagination.total)}{' '}
              of {pagination.total} matches
            </div>
          )}
        </>
      )}
    </div>
  );
}

export function CustomerProductMatchesPage() {
  const [filters, setFilters] = useState<Filters>({});

  return (
    <Page
      title="Customer Product Matches"
      subtitle="Shows which customer-specific terms map to which products. These mappings are created when processing sales orders."
    >
      <CustomerProductMatchesTable
        filters={filters}
        onFiltersChange={setFilters}
      />
    </Page>
  );
}
